import { Link } from 'react-router'
import type { JSX } from 'react'
import { useBrand } from '@/brand/context'
import { Lockup } from '@/shell/Lockup'

/**
 * What the product promises, in six lines — drafted from the home page.
 *
 * Each is a thing the system *does*, not a feature name: the person reading
 * this is deciding whether to sign in, and "Technical audit" tells them less
 * than what happens to their site every week.
 */
const POINTS: ReadonlyArray<{ glyph: string; text: string }> = [
  { glyph: '◔', text: 'A technical audit of your site every week, with the fixes made for you' },
  { glyph: '✎', text: 'Content written to rank, approved by you before it goes live' },
  { glyph: '◈', text: 'Keywords tracked against the sites you actually compete with' },
  { glyph: '◎', text: "LinkedIn discovery and outreach that respects the platform's limits" },
  { glyph: '▤', text: 'One inbox for every reply, on every channel' },
  { glyph: '≡', text: 'A reasoning trail for every decision the system makes' },
]

/**
 * The left half of every public screen.
 *
 * On the apex it is AugRank's pitch: the lockup, a two-line headline, the six
 * bullets and the footer line. On a tenant host it is the organisation's mark
 * and one muted line — no bullets, because a white-labelled login page is not
 * the place AugRank sells itself, and nothing the tenant did not write. On a
 * host nobody owns (`brand.host === 'unknown'`, `NoWorkspace`) it is neither:
 * the mark alone, with no pitch and no footer, because this is not AugRank
 * selling itself and not an organisation's page — it is nobody's.
 *
 * An `<aside>`, and its headline is a `<p>` rather than a heading: the page's
 * one `h1` is the form's title on the right (`structure.test.tsx` pins that
 * there is exactly one), and a heading in the panel would put a level-2 before
 * the level-1 in document order on every one of the twelve screens.
 *
 * Below 900px the panel collapses to a strip holding only the mark
 * (`.app-split__panel` in `app.css`) — the pitch is what a phone has no room
 * for, and the mark is what still says whose page this is.
 */
export function BrandPanel(): JSX.Element {
  const brand = useBrand()
  const tenant = brand.host === 'tenant'
  const name = brand.name ?? brand.productName

  return (
    <aside className="app-split__panel" aria-label={tenant ? name : 'About AugRank'}>
      <Link className="app-split__mark" to="/">
        {tenant ? (
          brand.logoUrl ? (
            <img className="app-split__logo" src={brand.logoUrl} alt={name} />
          ) : (
            <span className="app-split__word">{name}</span>
          )
        ) : (
          <Lockup size="2rem" />
        )}
      </Link>

      {tenant ? (
        <p className="app-split__welcome">Sign in to your workspace</p>
      ) : brand.host === 'unknown' ? null : (
        <div className="app-split__pitch">
          <p className="app-split__headline">{'Rank,\nthen reach.'}</p>
          <ul className="app-split__points">
            {POINTS.map((point) => (
              <li key={point.text}>
                <span className="app-split__glyph" aria-hidden="true">
                  {point.glyph}
                </span>
                <span>{point.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tenant || brand.host === 'unknown' ? null : (
        <p className="app-split__panelfoot">One workspace for SEO · Outreach · LinkedIn</p>
      )}
    </aside>
  )
}
